import { useState, useEffect, useCallback } from 'react';

// Supported routes in the application
export type AppRoute = '/' | '/complaint' | '/av-request';

/**
 * Custom hook for hash-based routing.
 * Reads the current route from window.location.hash (e.g. #/complaint)
 * and provides a navigate function to change routes.
 * Scrolls to top on route change and supports browser back/forward.
 */
export function useHashRoute(): {
  route: AppRoute;
  navigate: (to: AppRoute) => void;
} {
  const getRoute = (): AppRoute => {
    const hash = window.location.hash.slice(1) || '/';
    if (hash === '/complaint') return '/complaint';
    if (hash === '/av-request') return '/av-request';
    return '/';
  };

  const [route, setRoute] = useState<AppRoute>(getRoute);

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(getRoute());
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = useCallback((to: AppRoute) => {
    if (to === '/') {
      // Clear hash for clean homepage URL
      window.history.pushState(null, '', window.location.pathname);
      setRoute('/');
      window.scrollTo(0, 0);
    } else {
      window.location.hash = to;
    }
  }, []);

  return { route, navigate };
}
